import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { CheckCircle, Circle, BookOpen, Users, Activity, UserCircle, Sparkles, X, ChevronRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const OnboardingChecklist = ({ user, onNavigate, onDismiss }) => {
  const [checklist, setChecklist] = useState({
    assessment_completed: false,
    profile_completed: false,
    first_article_read: false,
    joined_community: false,
    tried_tracker: false
  });
  const [dismissed, setDismissed] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.id) fetchChecklist();
  }, [user?.id, user?.wellness_assessment]);

  const fetchChecklist = async () => {
    try {
      const response = await axios.get(`${API}/api/users/${user.id}/onboarding-checklist`);
      if (response.data) {
        setChecklist(response.data);
      }
    } catch (error) {
      // If endpoint doesn't exist yet, derive from user data
      setChecklist({
        assessment_completed: !!user.wellness_assessment,
        profile_completed: user.onboarding_completed && (user.fun_facts || user.wellness_goals?.length > 0),
        first_article_read: false,
        joined_community: false,
        tried_tracker: false
      });
    } finally {
      setLoading(false);
    }
  };

  const completedCount = Object.values(checklist).filter(Boolean).length;
  const totalItems = Object.keys(checklist).length;
  const allComplete = completedCount === totalItems;

  if (dismissed || loading || allComplete) return null;

  const items = [
    {
      key: 'assessment_completed',
      label: 'Take Wellness Assessment',
      description: 'Discover your primary focus area',
      icon: Sparkles,
      action: () => onNavigate('assessment'),
      color: 'text-burgundy'
    },
    {
      key: 'profile_completed',
      label: 'Complete Your Profile',
      description: 'Help the community get to know you',
      icon: UserCircle,
      action: () => onNavigate('profile'),
      color: 'text-burgundy'
    },
    {
      key: 'first_article_read',
      label: 'Read Your First Article',
      description: 'Explore the Classroom content',
      icon: BookOpen,
      action: () => onNavigate('classroom'),
      color: 'text-dark-teal'
    },
    {
      key: 'joined_community',
      label: 'Join a Discussion',
      description: 'Connect with other members',
      icon: Users,
      action: () => onNavigate('community'),
      color: 'text-dark-teal'
    },
    {
      key: 'tried_tracker',
      label: 'Try the Wellness Tracker',
      description: 'Start tracking how you feel',
      icon: Activity,
      action: () => onNavigate('tracker'),
      color: 'text-gold-dark'
    }
  ];

  const progressPercent = (completedCount / totalItems) * 100;

  return (
    <div className="bg-white rounded-xl border border-burgundy/15 shadow-sm overflow-hidden" data-testid="onboarding-checklist">
      {/* Header */}
      <div className="px-4 py-3 bg-gradient-to-r from-burgundy/5 to-dark-teal/5 border-b border-burgundy/10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div>
              <h3 className="font-serif font-bold text-burgundy text-sm">Getting Started</h3>
              <p className="text-burgundy/60 text-[11px]">{completedCount} of {totalItems} complete</p>
            </div>
            {/* Inline progress bar */}
            <div className="w-24 h-1.5 bg-burgundy/10 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-burgundy to-dark-teal rounded-full transition-all duration-500"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>
          <button
            onClick={() => { setDismissed(true); onDismiss?.(); }}
            className="text-burgundy/30 hover:text-burgundy/60 transition-colors"
            data-testid="checklist-dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Checklist Items - compact grid on desktop */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 divide-y sm:divide-y-0 divide-burgundy/5">
        {items.map(item => {
          const isComplete = checklist[item.key];
          const Icon = item.icon;
          return (
            <button
              key={item.key}
              onClick={isComplete ? undefined : item.action}
              className={`flex items-center gap-2 px-4 py-2.5 text-left transition-colors ${
                isComplete ? 'opacity-60' : 'hover:bg-cream/50 cursor-pointer'
              }`}
              disabled={isComplete}
              data-testid={`checklist-${item.key}`}
            >
              {isComplete ? (
                <CheckCircle className="h-4 w-4 text-dark-teal shrink-0" />
              ) : (
                <Circle className="h-4 w-4 text-burgundy/25 shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <p className={`text-xs font-medium ${isComplete ? 'text-burgundy/50 line-through' : 'text-burgundy'}`}>
                  {item.label}
                </p>
              </div>
              {!isComplete && <ChevronRight className="h-3 w-3 text-burgundy/30 shrink-0" />}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default OnboardingChecklist;
